import { useEffect, useRef, useState } from "react";
import { Siren, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

// Globaal NU!-alarm: pollt urgente taak-meldingen voor de ingelogde gebruiker
// en toont een fullscreen alarm met luid geluid + trilling tot het wordt weggeklikt.
export default function NudgeAlarm() {
  const { user } = useAuth();
  const t = useT();
  const [active, setActive] = useState(null);
  const seen = useRef(new Set());
  const audioRef = useRef(null);
  const beepTimer = useRef(null);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/tasks/nudges/active");
        if (stop) return;
        const fresh = (data || []).find((n) => !seen.current.has(n.id));
        (data || []).forEach((n) => seen.current.add(n.id));
        if (fresh) setActive((cur) => cur || fresh);
      } catch { /* stil */ }
    };
    poll();
    const iv = setInterval(poll, 12000);
    return () => { stop = true; clearInterval(iv); };
  }, [user]);

  useEffect(() => {
    if (!active) { stopAlarm(); return; }
    startAlarm();
    return stopAlarm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const startAlarm = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        audioRef.current = ctx;
        const beep = () => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "square";
          o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(0.0001, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
          o.frequency.setValueAtTime(880, ctx.currentTime);
          o.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.36);
          o.start();
          o.stop(ctx.currentTime + 0.38);
        };
        beep();
        beepTimer.current = setInterval(beep, 850);
      }
    } catch { /* geluid geblokkeerd */ }
    try { navigator.vibrate?.([300, 150, 300, 150, 600]); } catch { /* nvt */ }
  };

  const stopAlarm = () => {
    if (beepTimer.current) { clearInterval(beepTimer.current); beepTimer.current = null; }
    try { audioRef.current?.close?.(); } catch { /* nvt */ }
    audioRef.current = null;
    try { navigator.vibrate?.(0); } catch { /* nvt */ }
  };

  if (!active) return null;
  const claimAndClose = async () => {
    try {
      if (active.task_id) {
        await api.post(`/tasks/${active.task_id}/claim`);
        toast.success(t("Top! Deze taak staat nu op jouw naam 💪"));
      }
    } catch { /* stil — sluit sowieso */ }
    setActive(null);
  };
  return (
    <div className="fixed inset-0 z-[200] bg-red-600 flex flex-col items-center justify-center p-8 text-white text-center" data-testid="nudge-alarm">
      <Siren className="w-24 h-24 mb-6 animate-pulse" strokeWidth={2.5} />
      <p className="font-head font-black text-sm uppercase tracking-[0.2em] opacity-90">{active.actor_name} {t("vraagt")}</p>
      <h1 className="font-head font-black text-4xl sm:text-5xl mt-2 mb-4 leading-tight">{active.task_title}</h1>
      <p className="text-lg font-bold mb-8 max-w-md">{t("Deze taak moet NÚ gebeuren — het kan niet langer wachten!")}</p>
      <button data-testid="nudge-alarm-dismiss" onClick={claimAndClose}
        className="bg-white text-red-600 rounded-full px-8 py-4 font-head font-black text-xl flex items-center gap-2 active:scale-95 transition-transform shadow-2xl">
        <Check className="w-6 h-6" strokeWidth={3} /> {t("Oké, ik doe het!")}
      </button>
      <button data-testid="nudge-alarm-close" onClick={() => setActive(null)}
        className="mt-4 text-white/80 underline font-bold text-sm">
        {t("Alleen sluiten")}
      </button>
    </div>
  );
}
